/**
 * Inspectors responsible for an inspection. Prefer the assignedInspectors array
 * (new model — any one may complete it); fall back to the legacy single
 * assignedTo so readers work during/after the migration.
 */
export const inspectorIds = (insp) =>
  Array.isArray(insp?.assignedInspectors)
    ? insp.assignedInspectors
    : insp?.assignedTo
      ? [insp.assignedTo]
      : [];

/**
 * A check answered "No" — a deficiency. The per-check model stores the answer on
 * checks[].answer (mirrors functions/lib/inspectionModel).
 */
export const preQIsNo = (c) => c?.answer === "no";

/**
 * Instantiate a property's checklistTemplate into a fresh questionaire in the
 * per-check model: each area's checks[] are its questions, each starting
 * unanswered (answer:null) with no photos.
 */
export const instantiateQuestionaire = (checklistTemplate) =>
  (Array.isArray(checklistTemplate) ? checklistTemplate : []).map((item) => ({
    id: item?.id,
    area: item?.area || "",
    question: item?.question || "",
    image: item?.image || null,
    note: "",
    checks: Array.isArray(item?.checks)
      ? item.checks.map((c) => ({
          label: c?.label || "",
          answer: null,
          note: "",
          photos: [],
        }))
      : [],
  }));

/**
 * Canonical shape of a new `inspections` doc. Used by both the assign modal
 * (competiterList) and the scheduler so the two screens can't drift.
 *
 * Assignment lives on the property in the new model, so an inspection carries
 * `assignedInspectors` (an array — any one of them may complete it). Callers may
 * pass `assignedInspectors` directly, or a single `assignedTo` which is wrapped.
 *
 * `deadline` may be a dayjs/moment (antd DatePicker) or an ISO string, or null.
 */
export const buildInspectionDoc = ({
  property,
  assignedInspectors,
  assignedTo,
  deadline = null,
}) => ({
  propertyId: property?._id || property?.docId || property?.id || null,
  propertyName: property?.propertyName || "",
  assignedInspectors: Array.isArray(assignedInspectors)
    ? assignedInspectors
    : assignedTo
      ? [assignedTo]
      : Array.isArray(property?.assignedInspectors)
        ? property.assignedInspectors
        : [],
  status: "pending",
  // Carry the property's timezone so deadline math is anchored to its zone.
  timezone: property?.timezone || "America/Denver",
  // Instantiate the template into the per-pre-question model (also deep-copies,
  // so future template edits don't mutate this inspection).
  questionaire: instantiateQuestionaire(property?.checklistTemplate),
  deadline: deadline
    ? typeof deadline.toISOString === "function"
      ? deadline.toISOString()
      : deadline
    : null,
  createdAt: new Date().toISOString(),
});
